import { useState, useEffect, useMemo, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { ChatThread } from "@/components/admin/AdminChatThread";
import { MessageCircle, ArrowLeft, RefreshCw, Search, Inbox } from "lucide-react";

const REFRESH_MS = 20000;

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 604800)}sem`;
};

const AdminInbox = () => {
  const { getAuthHeaders } = useAuth();
  const [threads, setThreads] = useState([]);
  const [creators, setCreators] = useState({}); // username -> {name, avatar}
  const [subscribers, setSubscribers] = useState([]); // [{ creatorUsername, email, expiresAt, createdAt }]
  const [activeCreator, setActiveCreator] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Load creators (avatar + name) — cached, infrequent
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch("/api/creators");
        if (r.ok) {
          const d = await r.json();
          const arr = Array.isArray(d) ? d : d.creators || [];
          const map = {};
          arr.forEach((c) => {
            map[c.username] = { name: c.name, avatar: c.avatar };
          });
          if (!cancel) setCreators(map);
        }
      } catch (e) { console.error(e); }
    })();
    return () => { cancel = true; };
  }, []);

  const loadThreads = useCallback(async (opts = {}) => {
    if (!opts.silent) setRefreshing(true);
    try {
      const r = await fetch("/api/admin/jgfans/threads", { headers: { ...getAuthHeaders() } });
      if (r.ok) {
        const d = await r.json();
        setThreads(d.threads || []);
      }
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadThreads();
    const t = setInterval(() => loadThreads({ silent: true }), REFRESH_MS);
    return () => clearInterval(t);
  }, [loadThreads]);

  // Fetch ALL subscribers up-front so we can list fans-without-conversation per creator
  const loadSubscribers = useCallback(async () => {
    try {
      const r = await fetch("/api/admin/jgfans/subscribers", { headers: { ...getAuthHeaders() } });
      if (r.ok) {
        const d = await r.json();
        setSubscribers(d.subscribers || []);
      }
    } catch (e) { console.error(e); }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadSubscribers();
    const t = setInterval(loadSubscribers, REFRESH_MS * 3); // refresh subs less often
    return () => clearInterval(t);
  }, [loadSubscribers]);

  // Group threads by creator (also include creators that have subscribers but zero threads)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of threads) {
      const u = t.creatorUsername;
      if (!map.has(u)) map.set(u, { username: u, threads: [], totalUnread: 0, lastAt: t.lastAt });
      const g = map.get(u);
      g.threads.push(t);
      g.totalUnread += t.unread || 0;
      if (new Date(t.lastAt) > new Date(g.lastAt)) g.lastAt = t.lastAt;
    }
    // Add creators that have subscribers but no conversations yet
    for (const s of subscribers) {
      const u = (s.creatorUsername || "").toLowerCase();
      if (!u) continue;
      if (!map.has(u)) {
        map.set(u, {
          username: u,
          threads: [],
          totalUnread: 0,
          lastAt: s.createdAt || new Date(0).toISOString(),
          subscribersOnly: true,
        });
      }
    }
    // sort creators by lastAt desc
    return Array.from(map.values()).sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
  }, [threads, subscribers]);

  // Filtered threads for active creator
  const filteredThreads = useMemo(() => {
    if (!activeCreator) return [];
    const g = grouped.find((x) => x.username === activeCreator);
    const existing = g ? g.threads.slice() : [];
    // Build a set of emails that already have a thread
    const haveThread = new Set(existing.map((t) => (t.subscriberEmail || "").toLowerCase()));
    // Append subscribers of this creator who have NOT messaged yet (empty conversations admin can start)
    const empties = subscribers
      .filter((s) => (s.creatorUsername || "").toLowerCase() === activeCreator.toLowerCase())
      .filter((s) => !haveThread.has((s.email || "").toLowerCase()))
      .map((s) => ({
        creatorUsername: activeCreator,
        subscriberEmail: s.email,
        lastAt: s.createdAt || new Date(0).toISOString(),
        lastMessage: "",
        lastSender: null,
        unread: 0,
        isEmpty: true,
      }));
    let list = existing
      .sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt))
      .concat(empties.sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt)));
    const qq = q.trim().toLowerCase();
    if (qq) list = list.filter((t) => (t.subscriberEmail || "").toLowerCase().includes(qq));
    return list;
  }, [grouped, activeCreator, q, subscribers]);

  const totalUnread = useMemo(() => grouped.reduce((acc, g) => acc + g.totalUnread, 0), [grouped]);

  return (
    <AdminLayout>
      <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight inline-flex items-center gap-2">
            <Inbox className="w-6 h-6 text-pink-400" />
            Mensajes
          </h1>
          <p className="text-white/50 text-sm">
            Conversaciones de JGFans agrupadas por modelo.
            {totalUnread > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 text-xs font-bold border border-rose-500/30">
                {totalUnread} sin leer
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => loadThreads()}
          disabled={refreshing}
          data-testid="inbox-refresh"
          className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm inline-flex items-center gap-1.5 text-white/85"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {/* 3-pane layout */}
      <div className="grid md:grid-cols-12 gap-3 h-[calc(100vh-200px)] min-h-[500px]">
        {/* Pane 1: Models */}
        <aside
          className={`md:col-span-3 bg-white/5 border border-white/10 rounded-2xl overflow-hidden flex flex-col ${
            (activeCreator && !activeThread) ? "hidden md:flex" : ""
          } ${activeThread ? "hidden md:flex" : ""}`}
          data-testid="inbox-models-pane"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-[11px] uppercase tracking-wider text-white/40 font-bold">Modelos</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-white/40 text-sm p-4 text-center">Cargando…</p>
            ) : grouped.length === 0 ? (
              <div className="p-6 text-center">
                <MessageCircle className="w-10 h-10 text-white/20 mx-auto mb-2" />
                <p className="text-white/40 text-sm">Aún no hay conversaciones</p>
              </div>
            ) : (
              grouped.map((g) => {
                const meta = creators[g.username] || {};
                const isActive = activeCreator === g.username;
                const totalFans = subscribers.filter(
                  (s) => (s.creatorUsername || "").toLowerCase() === g.username.toLowerCase()
                ).length;
                return (
                  <button
                    key={g.username}
                    onClick={() => { setActiveCreator(g.username); setActiveThread(null); }}
                    data-testid={`inbox-creator-${g.username}`}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 transition border-b border-white/5 ${
                      isActive ? "bg-pink-500/10 border-l-2 border-l-pink-500" : "hover:bg-white/5"
                    }`}
                  >
                    <img
                      src={meta.avatar || "/logo.png"}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="w-9 h-9 rounded-full object-cover bg-white/5 ring-1 ring-pink-500/20 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{meta.name || g.username}</p>
                      <p className="text-[11px] text-white/45 truncate">
                        @{g.username} · {Math.max(totalFans, g.threads.length)} fans
                      </p>
                    </div>
                    {g.totalUnread > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold min-w-[20px] text-center">
                        {g.totalUnread}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Pane 2: Threads (fans) */}
        <section
          className={`md:col-span-4 bg-white/5 border border-white/10 rounded-2xl overflow-hidden flex flex-col ${
            !activeCreator ? "hidden md:flex" : ""
          } ${activeThread ? "hidden md:flex" : ""}`}
          data-testid="inbox-threads-pane"
        >
          <div className="px-3 py-2.5 border-b border-white/10 flex items-center gap-2">
            <button
              onClick={() => setActiveCreator(null)}
              className="md:hidden text-white/60 hover:text-white p-1"
              aria-label="Volver"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-white/40 font-bold">Fans</p>
              <p className="text-white text-sm font-bold truncate">
                {activeCreator ? (creators[activeCreator]?.name || activeCreator) : "Selecciona una modelo"}
              </p>
            </div>
          </div>
          {activeCreator && (
            <div className="px-3 pt-2 pb-2 border-b border-white/5">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2 text-white/30" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar email…"
                  className="w-full bg-black/40 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white placeholder-white/30 outline-none focus:border-pink-400/50"
                  data-testid="inbox-search"
                />
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
            {!activeCreator ? (
              <p className="text-white/40 text-sm p-6 text-center">← Elige una modelo a la izquierda</p>
            ) : filteredThreads.length === 0 ? (
              <p className="text-white/40 text-sm p-6 text-center">Sin fans aún</p>
            ) : (
              filteredThreads.map((t) => {
                const isActive = activeThread &&
                  activeThread.creatorUsername === t.creatorUsername &&
                  activeThread.subscriberEmail === t.subscriberEmail;
                const fromMe = t.lastSender === "creator";
                const previewIcon = fromMe ? "↗ " : "";
                if (t.isEmpty) {
                  return (
                    <button
                      key={`empty-${t.creatorUsername}-${t.subscriberEmail}`}
                      onClick={() => setActiveThread(t)}
                      data-testid={`inbox-thread-empty-${t.subscriberEmail}`}
                      className={`w-full text-left px-3 py-2.5 border-b border-white/5 transition ${
                        isActive ? "bg-pink-500/10" : "hover:bg-white/5"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-white/70 truncate flex-1">{t.subscriberEmail}</p>
                        <span className="text-[9px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-1.5 py-0.5 font-bold shrink-0">
                          Nuevo
                        </span>
                      </div>
                      <p className="text-[12px] text-white/35 italic truncate">
                        Sin mensajes — iniciar conversación
                      </p>
                    </button>
                  );
                }
                return (
                  <button
                    key={`${t.creatorUsername}-${t.subscriberEmail}`}
                    onClick={() => setActiveThread(t)}
                    data-testid={`inbox-thread-${t.subscriberEmail}`}
                    className={`w-full text-left px-3 py-2.5 border-b border-white/5 transition ${
                      isActive ? "bg-pink-500/10" : "hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-white truncate flex-1">{t.subscriberEmail}</p>
                      <span className="text-[10px] text-white/40">{timeAgo(t.lastAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className={`text-[12px] truncate flex-1 ${t.unread > 0 ? "text-white font-semibold" : "text-white/45"}`}>
                        {previewIcon}{t.lastMessage || "(media)"}
                      </p>
                      {t.unread > 0 && (
                        <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        {/* Pane 3: Active thread */}
        <section
          className={`md:col-span-5 min-h-0 ${!activeThread ? "hidden md:flex md:flex-col" : "flex flex-col"}`}
          data-testid="inbox-chat-pane"
        >
          {activeThread ? (
            <ChatThread
              thread={activeThread}
              getAuthHeaders={getAuthHeaders}
              onClose={() => setActiveThread(null)}
              fullHeight
              onSent={() => loadThreads({ silent: true })}
            />
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-2xl h-full flex items-center justify-center text-center px-6">
              <div>
                <MessageCircle className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60 text-sm">Selecciona una conversación</p>
                <p className="text-white/30 text-xs mt-1">Los mensajes se actualizan cada {REFRESH_MS / 1000}s</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
};

export default AdminInbox;
